import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2, Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Mowing",
  "Trimming & Edging",
  "Fertilization",
  "Full Season Bundle",
  "Other / Not sure",
];

const empty = {
  name: "",
  email: "",
  phone: "",
  address: "",
  service: SERVICES[0],
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, form);
      setSuccess(true);
      setForm(empty);
      toast.success("Quote request sent. Christian will reach out shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us instead.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-[#F4F3EF]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Left: Direct info */}
        <div className="lg:col-span-5">
          <div className="font-mono-accent text-[11px] tracking-[0.25em] uppercase text-[#E64C23] mb-5">
            / Get a quote
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0B1C14] leading-[0.95]">
            Let&apos;s
            <br />
            <span className="italic">talk turf.</span>
          </h2>
          <p className="mt-6 text-[#4A5D53] text-base sm:text-lg leading-relaxed max-w-md">
            Tell us about your property and we&apos;ll get back the same day
            with a flat, honest quote. No pressure — just numbers.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center border-2 border-[#0B1C14] bg-white shrink-0">
                <Phone size={18} strokeWidth={2.5} className="text-[#0B1C14]" />
              </span>
              <div>
                <div className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53]">
                  Call or text
                </div>
                <a
                  href="tel:+12314635080"
                  data-testid="contact-phone-link"
                  className="font-display text-2xl uppercase tracking-tight text-[#0B1C14] hover:text-[#E64C23] transition-colors"
                >
                  (231) 463‑5080
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center border-2 border-[#0B1C14] bg-white shrink-0">
                <Mail size={18} strokeWidth={2.5} className="text-[#0B1C14]" />
              </span>
              <div>
                <div className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53]">
                  Email
                </div>
                <a
                  href="mailto:crosscutlawntc@gmail.com"
                  data-testid="contact-email-link"
                  className="font-display text-xl uppercase tracking-tight text-[#0B1C14] hover:text-[#E64C23] transition-colors break-all"
                >
                  crosscutlawntc@gmail.com
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center border-2 border-[#0B1C14] bg-white shrink-0">
                <MapPin size={18} strokeWidth={2.5} className="text-[#0B1C14]" />
              </span>
              <div>
                <div className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53]">
                  Service area
                </div>
                <div className="font-display text-xl uppercase tracking-tight text-[#0B1C14]">
                  Traverse City &amp; Grand Traverse County, MI
                </div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center border-2 border-[#0B1C14] bg-white shrink-0">
                <Instagram size={18} strokeWidth={2.5} className="text-[#0B1C14]" />
              </span>
              <div>
                <div className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53]">
                  Follow the work
                </div>
                <a
                  href="https://www.instagram.com/crosscut.lawncare"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="font-display text-xl uppercase tracking-tight text-[#0B1C14] hover:text-[#E64C23] transition-colors"
                >
                  @crosscut.lawncare
                </a>
              </div>
            </li>
          </ul>

          <div className="mt-10 border-2 border-[#0B1C14] p-5 bg-[#EAE8DF]">
            <div className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53]">
              Hours
            </div>
            <div className="mt-1 font-display text-lg uppercase tracking-tight text-[#0B1C14]">
              Mon – Sat · 7 AM – 7 PM
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-2 bg-[#E64C23] -z-0" aria-hidden />
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="relative z-10 bg-white border-2 border-[#0B1C14] p-7 sm:p-10"
            >
              {success ? (
                <div
                  data-testid="quote-form-success"
                  className="py-10 text-center"
                >
                  <div className="mx-auto w-16 h-16 flex items-center justify-center border-2 border-[#0B1C14] bg-[#EAE8DF]">
                    <CheckCircle2
                      className="text-[#E64C23]"
                      size={32}
                      strokeWidth={2.5}
                    />
                  </div>
                  <h3 className="mt-6 font-display uppercase text-3xl tracking-tight text-[#0B1C14]">
                    Quote on its way
                  </h3>
                  <p className="mt-3 text-[#4A5D53] max-w-md mx-auto">
                    Thanks for the info — we&apos;ll reach out within the day.
                    For anything urgent, give us a ring.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="quote-form-reset"
                    className="btn-secondary mt-8"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-7">
                    <h3 className="font-display uppercase text-2xl sm:text-3xl tracking-tight text-[#0B1C14]">
                      Request a free quote
                    </h3>
                    <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53] hidden sm:block">
                      Takes 60 seconds
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full name *">
                      <input
                        name="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={onChange}
                        placeholder="Jane Smith"
                        className="input-craft"
                        data-testid="quote-form-name"
                      />
                    </Field>
                    <Field label="Email *">
                      <input
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={onChange}
                        placeholder="you@email.com"
                        className="input-craft"
                        data-testid="quote-form-email"
                      />
                    </Field>
                    <Field label="Phone *">
                      <input
                        name="phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={onChange}
                        placeholder="(231) 555‑0123"
                        className="input-craft"
                        data-testid="quote-form-phone"
                      />
                    </Field>
                    <Field label="Property address">
                      <input
                        name="address"
                        type="text"
                        value={form.address}
                        onChange={onChange}
                        placeholder="123 South Creek"
                        className="input-craft"
                        data-testid="quote-form-address"
                      />
                    </Field>
                    <Field label="Service needed" className="sm:col-span-2">
                      <select
                        name="service"
                        value={form.service}
                        onChange={onChange}
                        className="input-craft"
                        data-testid="quote-form-service"
                      >
                        {SERVICES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label="Anything we should know?"
                      className="sm:col-span-2"
                    >
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        placeholder="Lot size, gate codes, hills, pets, preferred stripe pattern…"
                        rows={4}
                        className="input-craft resize-none"
                        data-testid="quote-form-message"
                      />
                    </Field>
                  </div>

                  <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                    <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#4A5D53] max-w-xs">
                      We&apos;ll only use your info to send your quote.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="quote-form-submit"
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Sending…" : "Send Request"}
                      {!submitting && (
                        <ArrowRight size={16} strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#0B1C14] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
